import { InputBase, styled } from '@mui/material';
import { forwardRef, useEffect, useRef } from 'react';
import Chip from 'shell/components/Chip';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';

const ContainerStack = styled(Stack)(({ error, hasValue, theme }) => {
    return {
        flexWrap: 'wrap',
        border: `1px solid ${theme.palette.ensightful.neutral[500]}`,
        borderRadius: '16px',
        width: '100%',
        maxHeight: '200px',
        padding: theme.spacing('7px', 3),
        background: theme.palette.ensightful.shades[0],
        cursor: 'text',
        overflowY: 'auto',
        '&:focus-within': {
            borderColor: theme.palette.ensightful.primary[700],
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
            ...(error && {
                borderColor: theme.palette.ensightful.error[700],
            }),
        },
        '&:hover': {
            borderColor: theme.palette.ensightful.primary[700],
            ...(error && {
                borderColor: theme.palette.ensightful.error[700],
            }),
        },
        ...(error && {
            borderColor: theme.palette.ensightful.error[700],
        }),
        ...(hasValue && {
            padding: theme.spacing(1),
        }),
    };
});

const CustomInputBase = styled(InputBase)(({ theme }) => {
    return {
        flex: 1,
        minWidth: '5rem',
        fontSize: `${theme.typography.p2.fontSize.large}rem`,
        fontWeight: theme.typography.p2.fontWeight.regular,
        letterSpacing: `${theme.typography.p2.letterSpacing.large}rem`,
        lineHeight: `${theme.typography.getLineHeight('p2', 'large')}rem`,
        '& > input': {
            padding: 0,
            '&::placeholder': {
                color: theme.palette.ensightful.neutral[500],
                opacity: 1,
            },
        },
    };
});

const MultiInput = forwardRef(
    ({ error, helperText, inputValue, placeholder, value, onChange, onAddChip, onRemoveChip, ...rest }, ref) => {
        const rootRef = useRef(null);
        const inputRef = useRef(null);

        const scrollTop = () => {
            if (rootRef.current) rootRef.current.scrollTop = 0;
        };

        const scrollToBottom = () => {
            if (rootRef.current) {
                const { height } = rootRef.current.getBoundingClientRect();
                rootRef.current.scrollTop = height;
            }
        };

        const onMouseDown = (e) => {
            if (e.target !== inputRef.current) e.preventDefault();
            inputRef.current.focus();
        };

        const handleFocus = () => {
            scrollToBottom();
        };

        const handleBlur = () => {
            if (inputValue) onAddChip(inputValue);
            scrollTop();
        };

        const handleKeyDown = (e) => {
            if (!inputValue && e.key === 'Backspace') {
                const [last] = value.slice(-1);
                if (last) onRemoveChip(last, value.length - 1);
            }

            if (inputValue.trim() && (e.key === 'Enter' || e.key === ',')) {
                e.preventDefault();
                onAddChip(e.target.value);
            }
        };

        useEffect(() => {
            scrollToBottom();
        }, [value]);

        return (
            <Stack ref={ref} {...rest} spacing={1}>
                <ContainerStack
                    ref={rootRef}
                    alignItems={'center'}
                    direction={'row'}
                    error={error}
                    hasValue={value.length > 0}
                    spacing={1}
                    onMouseDown={onMouseDown}
                >
                    {value.map((item, index) => {
                        return (
                            <Chip
                                key={item.key}
                                color={item.error ? 'error' : 'default'}
                                content={item.value}
                                loading={item.loading}
                                onClick={() => onRemoveChip(item, index)}
                            />
                        );
                    })}
                    <CustomInputBase
                        inputRef={inputRef}
                        placeholder={value.length ? '' : placeholder}
                        value={inputValue}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onChange={onChange}
                        onKeyDown={handleKeyDown}
                    />
                </ContainerStack>
                {helperText && (
                    <Text color={error ? 'error' : 'light'} variant={'p3'}>
                        {helperText}
                    </Text>
                )}
            </Stack>
        );
    }
);

export default MultiInput;
